import Grid from '@mui/material/Grid';
import { FilterSingleLead } from 'src/stores/LeadStore';
import { useState } from 'react';
import { v4 as uuid } from 'uuid';
import { Filter, FilterType } from '../filters/FilterTypes';
import LeadSearch from '../filters/leads/LeadSearch';
import { leadEndpoints } from '../../configs/endpoints';
import GlobalUpdateGrid from './GlobalUpdateGrid';
import { Box, Button, Drawer, makeStyles, Typography } from '@mui/material';
import PageHeader from '../../materio/@core/components/page-header';
import UpdateFieldsForm from './UpdateFieldsForm';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { v4 as uuidv4 } from 'uuid';
import { yupResolver } from '@hookform/resolvers/yup';
import { UpdateRequest } from './GlobalUpdateTypes';
import useGlobalUpdate from './useGlobalUpdate';
import { GridSelectionModel } from '@mui/x-data-grid-pro';
import { client } from '../../utils/axios-client';
import GlobalUpdateProgressDialog from './GlobalUpdateProgressDialog';
import ConfirmationDialog from '../common/ConfirmationDialog';
import UpgradeIcon from '@mui/icons-material/Upgrade';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import RenderIf, { ClaimType, ClaimValue } from '../common/RenderIf';
import LeadDataGridView from '../Components/LeadDataGridView';
import { useGetMassUpdateItems } from '../../hooks/mass-update.hooks';
import SectionAppBar from '../../v2/components/Section/SectionAppBar';

const schema = yup.object().shape({});

const GlobalUpdate = () => {
    const { getNewRequest } = useGlobalUpdate();
    const [updateRequest, setUpdateRequest] = useState(getNewRequest());
    const [selectedLeadIds, setSelectedLeadIds] = useState<string[]>([]);
    const [openProgress, setOpenProgress] = useState(false);
    const [confirmUpdateOpen, setConfirmUpdateOpen] = useState(false);
    const navigate = useNavigate();

    const {
        reset,
        control,
        setError,
        setValue,
        handleSubmit,
        getValues,
        formState: { errors },
    } = useForm({
        defaultValues: updateRequest,
        mode: 'onChange',
        resolver: yupResolver(schema),
    });

    const filterSingleLead = FilterSingleLead.useState((s) => s.filterSingleLead);
    const [filter, setFilter] = useState({
        id: uuid(),
        description: '',
        searchTerm: '',
        options: [],
        activityDate: 'any',
        filterType: FilterType.User,
    } as Filter);

    const [pageSize, setPageSize] = useState(10);
    const {
        data: paginatedLeads,
        isLoading,
        fetchNextPage,
        hasNextPage,
        refetch,
    } = useGetMassUpdateItems(filter.options, filter.searchTerm, pageSize);

    const onFiltersChanged = (newFilter: Filter) => {
        setFilter(newFilter);
    };

    const onSubmit = async (data: UpdateRequest) => {
        setConfirmUpdateOpen(true);
        setUpdateRequest(data);
    };

    const handleConfirmUpdate = () => {
        setConfirmUpdateOpen(false);
        setOpenProgress(true);
    };

    const handleClose = () => {
        reset(getNewRequest());
    };

    const onReset = () => {
        const newReq = getNewRequest();
        setUpdateRequest(newReq);
        reset(newReq);
    };

    const onSelectionModeChange = async (ids: GridSelectionModel) => {
        const selectedIds = ids.map((s) => s.toString());
        setSelectedLeadIds(selectedIds);
    };

    const handleCloseProgressDialog = (hasUpdated: boolean) => {
        setOpenProgress(false);

        if (hasUpdated) {
            const newReq = getNewRequest();
            setUpdateRequest(newReq);
            reset(newReq);
            setSelectedLeadIds([]);
        }
    };

    return (
        <>
            <Drawer
                open={true}
                onClose={() => {}}
                PaperProps={{
                    sx: { width: '100%', padding: 0 },
                }}
            >
                <SectionAppBar
                    title="Mass Update"
                    subTitle=""
                    buttons={
                        <>
                            <Button
                                size="small"
                                variant="contained"
                                color="info"
                                onClick={handleSubmit(onSubmit)}
                                endIcon={<AutoFixHighIcon />}
                            >
                                Update {selectedLeadIds.length} Leads
                            </Button>
                            <RenderIf claimType={ClaimType.GlobalUpdates} claimValue={ClaimValue.Reset}>
                                <Button size="small" variant="contained" onClick={onReset} endIcon={<RefreshIcon />}>
                                    Reset
                                </Button>
                            </RenderIf>
                            <Button
                                size="small"
                                variant="contained"
                                endIcon={<CloseIcon />}
                                onClick={() => navigate('/')}
                            >
                                Close
                            </Button>
                        </>
                    }
                />
                <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', padding: 5 }}>
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'row' }}>
                        <Box sx={{ flexGrow: 1 }}>
                            {!filterSingleLead && <LeadSearch onChange={onFiltersChanged} height="auto" />}

                            <LeadDataGridView
                                checkboxSelection={true}
                                isLoading={isLoading}
                                leadPages={paginatedLeads?.pages}
                                hasNextPage={hasNextPage === true}
                                fetchNextPage={fetchNextPage}
                                preColumns={[]}
                                selectedLeadIds={selectedLeadIds}
                                onSelectionModeChange={onSelectionModeChange}
                                key={`dg-${updateRequest.uniqueKey}`}
                                onLeadClick={() => {}}
                            />
                            {/*<GlobalUpdateGrid filter={filter} searchTerm={filter.searchTerm} queryRecordsEndpoint={leadEndpoints.getLeads} selectedLeadIds={selectedLeadIds} onSelectionModeChange={onSelectionModeChange} key={`dg-${updateRequest.uniqueKey}`} />*/}
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', maxWidth: '35%' }}>
                            <Box sx={{ paddingTop: 5 }}>
                                <UpdateFieldsForm
                                    setValue={setValue}
                                    getValues={getValues}
                                    onSubmit={handleSubmit(onSubmit)}
                                    handleClose={handleClose}
                                    control={control}
                                    errors={errors}
                                    updateRequest={updateRequest}
                                    key={`fm-${updateRequest.uniqueKey}`}
                                />
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </Drawer>

            <ConfirmationDialog
                open={confirmUpdateOpen}
                onCancel={() => setConfirmUpdateOpen(false)}
                onConfirm={handleConfirmUpdate}
                title="Confirm Mass Update"
                message={`Are you sure you want to update ${selectedLeadIds.length} leads?`}
                confirmButtonText="Update"
                cancelButtonText="Cancel Update"
            />
            <GlobalUpdateProgressDialog
                open={openProgress}
                onClose={handleCloseProgressDialog}
                leadIds={selectedLeadIds}
                updateRequest={updateRequest}
                key={updateRequest.uniqueKey}
            />
        </>
    );
};

GlobalUpdate.guestGuard = false;
GlobalUpdate.authGuard = true;

export { GlobalUpdate };

export default GlobalUpdate;
